import {
  APP_PALETTES,
  PageHeader,
  PageSection,
  PalettePicker,
  SettingCard,
  SettingList,
  SettingRadioGroup,
  type AppPalette,
} from "@ceedero/ui";

import { AppPage } from "@/components/app-page";
import { useLanguageStore } from "@/localization/store/use-language-store";
import { useTranslation, type TranslationKey } from "@/localization/hooks/use-translation";
import { useDisplaySettingsStore } from "@/settings/store/use-display-settings-store";
import { formatAlbumLength } from "@/settings/utils/format-album-length";
import { useThemeStore } from "@/settings/store/use-theme-store";

/**
 * The album each length format is demonstrated on: 67 minutes and 12 seconds,
 * which is long enough for the two spellings to actually differ.
 */
const ALBUM_LENGTH_EXAMPLE_SECONDS = 4_032;

const PALETTE_NAME_KEYS = {
  violet: "settings.palette.violet",
  vermilion: "settings.palette.vermilion",
  teal: "settings.palette.teal",
  aubergine: "settings.palette.aubergine",
  forest: "settings.palette.forest",
  brass: "settings.palette.brass",
  cobalt: "settings.palette.cobalt",
  red: "settings.palette.red",
} satisfies Record<AppPalette, TranslationKey>;

/**
 * How the application looks and what language it speaks.
 *
 * All of it belongs to this browser rather than to the account, and none of it
 * is sent anywhere. Somebody signing in on a borrowed machine should get that
 * machine's language and that machine's palette, not the ones the account
 * holder chose on their own — which is also why these are stored here and read
 * from nothing else.
 *
 * Laid out as the desktop's Settings page is — one list of rows per section,
 * what the setting is on the left and its control on the right — so the same
 * choice looks the same in both.
 */
export const SettingsPage = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguageStore();
  const { preference, setPreference, palette, setPalette } = useThemeStore();
  const { albumLengthFormat, setAlbumLengthFormat, boardPattern, setBoardPattern } =
    useDisplaySettingsStore();

  const paletteNames = Object.fromEntries(
    APP_PALETTES.map((option) => [option, t(PALETTE_NAME_KEYS[option])]),
  ) as Record<AppPalette, string>;

  return (
    <AppPage
      width="wide"
      header={<PageHeader title={t("settings.title")} description={t("settings.description")} />}
    >
      <PageSection
        title={t("settings.appearance.title")}
        description={t("settings.appearance.description")}
      >
        <SettingList>
          <SettingCard
            title={t("settings.palette.title")}
            description={t("settings.palette.description")}
            isStacked
          >
            <PalettePicker
              label={t("settings.palette.label")}
              value={palette}
              onValueChange={setPalette}
              paletteNames={paletteNames}
            />
          </SettingCard>
          <SettingCard
            title={t("settings.theme.title")}
            description={t("settings.theme.description")}
          >
            <SettingRadioGroup
              label={t("settings.theme.title")}
              value={preference}
              onValueChange={setPreference}
              options={[
                { value: "light", label: t("settings.theme.light") },
                { value: "dark", label: t("settings.theme.dark") },
                { value: "system", label: t("settings.theme.system") },
              ]}
            />
          </SettingCard>
          <SettingCard
            title={t("settings.boardPattern.title")}
            description={t("settings.boardPattern.description")}
          >
            <SettingRadioGroup
              label={t("settings.boardPattern.title")}
              value={boardPattern}
              onValueChange={setBoardPattern}
              options={[
                { value: "dots", label: t("settings.boardPattern.dots") },
                { value: "lines", label: t("settings.boardPattern.lines") },
              ]}
            />
          </SettingCard>
        </SettingList>
      </PageSection>

      <PageSection
        title={t("settings.general.title")}
        description={t("settings.general.description")}
      >
        <SettingList>
          <SettingCard
            title={t("settings.language.title")}
            description={t("settings.language.description")}
          >
            {/*
              Each language names itself, in itself. "Lithuanian" is of no use to
              somebody who cannot read the English it is written in, which is
              exactly the person reaching for this control.
            */}
            <SettingRadioGroup
              label={t("settings.language.label")}
              value={language}
              onValueChange={setLanguage}
              options={[
                { value: "en", label: "English" },
                { value: "lt", label: "Lietuvių" },
              ]}
            />
          </SettingCard>
          <SettingCard
            title={t("settings.albumLength.title")}
            description={t("settings.albumLength.description")}
          >
            {/*
              Each format is shown rather than described, in the language the
              page is being read in and through the same formatter the catalog
              writes its totals with — so a label cannot promise a spelling the
              application does not use.
            */}
            <SettingRadioGroup
              label={t("settings.albumLength.title")}
              value={albumLengthFormat}
              onValueChange={setAlbumLengthFormat}
              options={(["minutes", "hours"] as const).map((format) => ({
                value: format,
                label: t(`settings.albumLength.${format}`, {
                  example: formatAlbumLength({
                    format,
                    language,
                    seconds: ALBUM_LENGTH_EXAMPLE_SECONDS,
                  }),
                }),
              }))}
            />
          </SettingCard>
        </SettingList>
      </PageSection>
    </AppPage>
  );
};
